import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Music2,
  Wand2,
  Save,
  Pencil,
  Plus,
  Trash2,
  Download,
  Printer,
  Mail,
  ArrowUp,
  ArrowDown,
  X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtTime = (t) => {
  if (!Number.isFinite(t) || t < 0) t = 0;
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const SECTION_PRESETS = ["INTRO", "COUPLET", "REFRAIN", "PONT", "SOLO", "OUTRO"];

/**
 * Turns an axios error into a user-readable single line.
 * Discriminates:
 *   - server response (any 4xx/5xx)  → "HTTP <status> — <detail>"
 *   - request sent, no response       → "Network Error — <URL>" (proxy 502 / timeout / DNS)
 *   - other                           → e.message
 */
const humanizeAxiosError = (e, urlHint = "") => {
  if (e?.response) {
    const status = e.response.status;
    const detail = e.response.data?.detail || e.response.data?.error || e.response.statusText || "";
    return `HTTP ${status}${detail ? " — " + String(detail).slice(0, 240) : ""}`;
  }
  if (e?.request) {
    return `Pas de réponse du serveur (${urlHint || "URL inconnue"}). Probable timeout ou crash du worker.`;
  }
  return e?.message || "Erreur inconnue";
};

/**
 * Accords — automatic chord-grid extractor.
 *
 * Source : the song's Playback audio (no manual file selection ever).
 * Flow   : Analyser → Sauvegarder/PDF → Éditer.
 */
export const ChordsDialog = ({ open, onOpenChange, song, axiosInstance, onSaved }) => {
  const [view, setView] = React.useState("idle"); // idle | analyzing | result | editor
  const [progress, setProgress] = React.useState(0);
  const [error, setError] = React.useState("");
  const [chordsData, setChordsData] = React.useState(null);
  const [diag, setDiag] = React.useState(null);
  const pollRef = React.useRef(null);

  React.useEffect(() => {
    if (!open || !song) return;
    setError("");
    setDiag(null);
    if (song.chords_data && song.chords_data.items?.length) {
      setChordsData(song.chords_data);
      setView("result");
    } else {
      setChordsData(null);
      setView("idle");
      // Fetch the exact filename the backend will use, so the user can see
      // there's no wildcard / no name mismatch.
      axiosInstance
        .get(`${API}/songs/${song.id}/chords/diag`)
        .then((r) => setDiag(r.data))
        .catch(() => setDiag(null));
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, song?.id]);

  const playbackName = diag?.resolved_for_analysis
    || song?.audio_playback_filename
    || song?.audio_filename;

  const runAnalysis = async () => {
    if (!song?.id) return;
    setError("");
    setProgress(0);
    setView("analyzing");
    let stalledChecks = 0;
    let lastProgress = -1;
    let pollErrors = 0;
    // Debug — keep them; the user reads the console when something fails on stage.
    const analyzeUrl = `${API}/songs/${song.id}/chords/analyze`;
    console.log("[Accords] Song ID:", song.id);
    console.log("[Accords] Playback filename:", playbackName);
    console.log("[Accords] Analysis URL:", analyzeUrl);
    try {
      const { data } = await axiosInstance.post(analyzeUrl);
      console.log("[Accords] Analyse lancée, job_id =", data?.job_id, "bytes =", data?.audio_size_bytes);
      const jobId = data.job_id;
      pollRef.current = setInterval(async () => {
        try {
          const { data: st } = await axiosInstance.get(`${API}/chord-jobs/${jobId}`);
          pollErrors = 0;
          if (st.progress === lastProgress) {
            stalledChecks += 1;
          } else {
            stalledChecks = 0;
            lastProgress = st.progress;
          }
          setProgress(st.progress || 0);
          if (st.status === "done") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setChordsData(st.result);
            setView("result");
            onSaved?.();
          } else if (st.status === "error") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setError(st.error || "Analyse échouée (le serveur a remonté une erreur sans détail)");
            setView("idle");
          } else if (stalledChecks > 120) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setError("L'analyse semble bloquée >72 s. Le serveur a peut-être manqué de mémoire — réessaye avec un fichier WAV plus court (<6 min).");
            setView("idle");
          }
        } catch (e) {
          pollErrors += 1;
          console.error("[Accords] Polling error:", e);
          if (e?.response?.status === 404) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setError("Le serveur a redémarré pendant l'analyse. Relance l'analyse.");
            setView("idle");
          } else if (pollErrors > 6) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setError(humanizeAxiosError(e, `GET ${API}/chord-jobs/${jobId}`));
            setView("idle");
          }
        }
      }, 600);
    } catch (e) {
      console.error("[Accords] POST /analyze failed:", e);
      setError(humanizeAxiosError(e, `POST ${analyzeUrl}`));
      setView("idle");
    }
  };

  const saveAndExportPdf = async () => {
    if (!song?.id || !chordsData) return;
    try {
      await axiosInstance.put(`${API}/songs/${song.id}/chords`, { chords_data: chordsData });
      onSaved?.();
      const res = await axiosInstance.get(`${API}/songs/${song.id}/chords/export.pdf`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  const openPdf = async () => {
    if (!song?.id) return;
    try {
      const res = await axiosInstance.get(`${API}/songs/${song.id}/chords/export.pdf`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  const sendByMail = () => {
    if (!chordsData?.items?.length) return;
    const lines = [
      `${song?.id} · ${song?.title}`,
      `Artiste : ${song?.artist || song?.composer || ""}`,
      `Tempo : ${chordsData.bpm || song?.bpm || ""} BPM   ·   Mesure : ${song?.beat || "4/4"}   ·   Tonalité : ${chordsData.key || song?.key || ""}`,
      "",
      "GRILLE D'ACCORDS",
      "",
    ];
    let currentSection = "";
    for (const it of chordsData.items) {
      if (it.section && it.section !== currentSection) {
        currentSection = it.section;
        lines.push("");
        lines.push(`[${currentSection}]`);
      }
      lines.push(`  ${fmtTime(it.time)}   ${it.chord}`);
    }
    const body = encodeURIComponent(lines.join("\n"));
    const subject = encodeURIComponent(`Grille d'accords — ${song?.id} ${song?.title}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[92vh] overflow-y-auto bg-[#0d0d0d] text-white border-white/10"
        data-testid="chords-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-lg text-[var(--gl-gold)] font-display flex items-center gap-2">
            <Music2 size={18} /> Accords · {song?.id}
          </DialogTitle>
          <DialogDescription className="text-neutral-400 text-xs">
            Analyse automatique de la grille d&apos;accords à partir du fichier <b>Playback</b>{playbackName ? "" : " (manquant)"}.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="bg-red-950/40 border border-red-700/40 text-red-200 text-xs px-3 py-2 rounded-lg" data-testid="chords-error">
            {error}
          </div>
        )}

        {view === "idle" && (
          <IdleView playbackName={playbackName} diag={diag} onAnalyze={runAnalysis} />
        )}

        {view === "analyzing" && <AnalysingView progress={progress} />}

        {(view === "result" || view === "editor") && chordsData && (
          <>
            <MetaBar song={song} chordsData={chordsData} />
            {view === "result" ? (
              <GridView chordsData={chordsData} />
            ) : (
              <EditorView chordsData={chordsData} onChange={setChordsData} />
            )}
            <ActionBar
              isEditing={view === "editor"}
              onToggleEdit={() => setView(view === "editor" ? "result" : "editor")}
              onAnalyzeAgain={runAnalysis}
              onSaveAndPdf={saveAndExportPdf}
              onOpenPdf={openPdf}
              onMail={sendByMail}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

/* ----- Subviews ----- */
const IdleView = ({ playbackName, onAnalyze }) => (
  <div className="space-y-3">
    <div className="bg-black border border-white/10 rounded-xl p-4 text-sm text-neutral-300">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2 font-bold">
        Source d&apos;analyse
      </div>
      <div className="font-mono text-xs break-all">
        {playbackName || (
          <span className="text-red-400">Aucun fichier Playback. Charge-en un dans la zone audio.</span>
        )}
      </div>
    </div>
    <button
      onClick={onAnalyze}
      disabled={!playbackName}
      className="w-full rounded-xl bg-[var(--gl-gold)] text-black font-bold uppercase tracking-wider text-sm py-3 flex items-center justify-center gap-2 disabled:opacity-40 hover:brightness-110"
      data-testid="chords-analyze-btn"
    >
      <Wand2 size={16} /> Analyser
    </button>
    <p className="text-[10px] text-neutral-500 text-center">
      G!GLive26 lit automatiquement le fichier Playback · BPM · Tonalité · Accords détectés à la volée.
    </p>
  </div>
);

const AnalysingView = ({ progress }) => (
  <div className="space-y-3 py-4" data-testid="chords-analyzing-view">
    <div className="text-center text-sm uppercase tracking-widest text-neutral-400 font-bold">
      Analyse harmonique en cours…
    </div>
    <div className="w-full h-3 bg-white/10 rounded-full overflow-hidden">
      <div
        className="h-full bg-[var(--gl-gold)]"
        style={{ width: `${progress}%`, transition: "width 200ms linear" }}
        data-testid="chords-progress-bar"
      />
    </div>
    <div className="text-center text-2xl font-display tabular-nums text-[var(--gl-gold)]">
      {progress}%
    </div>
    <p className="text-[10px] text-neutral-500 text-center">
      Détection BPM · Tonalité · Accords majeurs/mineurs (CQT + template matching).
    </p>
  </div>
);

const MetaBar = ({ song, chordsData }) => (
  <div
    className="bg-black border border-white/10 rounded-xl px-4 py-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs"
    data-testid="chords-meta"
  >
    <Meta label="Tempo" value={`${Math.round(chordsData.bpm || 0)} BPM`} />
    <Meta label="Mesure" value={song?.beat || "4/4"} />
    <Meta label="Tonalité" value={chordsData.key || "—"} />
    <Meta label="Durée" value={fmtTime(chordsData.duration || song?.duration_seconds || 0)} />
  </div>
);

const Meta = ({ label, value }) => (
  <div>
    <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold">
      {label}
    </div>
    <div className="text-base font-display text-white tabular-nums">{value}</div>
  </div>
);

const GridView = ({ chordsData }) => {
  const groups = React.useMemo(() => groupBySection(chordsData.items || []), [chordsData]);
  if (!groups.length) {
    return (
      <div className="text-neutral-500 text-sm py-6 text-center">
        Aucun accord. Re-lance l&apos;analyse ou édite manuellement.
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="chords-grid-view">
      {groups.map((g, gi) => (
        <div key={gi}>
          {g.name && (
            <div className="text-[10px] uppercase tracking-widest text-[var(--gl-gold)] font-bold mb-1.5">
              {g.name}
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 border border-white/10 rounded-lg overflow-hidden">
            {g.items.map((it, i) => (
              <div
                key={i}
                className="border-r border-b border-white/10 last:border-r-0 px-3 py-3 text-center bg-black"
                data-testid="chords-cell"
              >
                <div className="text-2xl font-display font-bold text-white">{it.chord}</div>
                <div className="text-[10px] text-neutral-500 tabular-nums">{fmtTime(it.time)}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const EditorView = ({ chordsData, onChange }) => {
  const items = chordsData.items || [];
  const update = (idx, patch) => {
    const next = items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    onChange({ ...chordsData, items: next });
  };
  const remove = (idx) => onChange({ ...chordsData, items: items.filter((_, i) => i !== idx) });
  const move = (idx, delta) => {
    const j = idx + delta;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange({ ...chordsData, items: next });
  };
  const insertAfter = (idx) => {
    const t0 = items[idx]?.time ?? 0;
    const t1 = items[idx + 1]?.time ?? (t0 + 2);
    const next = items.slice();
    next.splice(idx + 1, 0, { time: +((t0 + t1) / 2).toFixed(2), chord: "C" });
    onChange({ ...chordsData, items: next });
  };

  return (
    <div className="space-y-2" data-testid="chords-editor-view">
      <div className="grid grid-cols-[80px_1fr_140px_auto] gap-2 px-2 text-[10px] uppercase tracking-widest text-neutral-500 font-bold">
        <div>Temps</div>
        <div>Accord</div>
        <div>Section</div>
        <div></div>
      </div>
      <div className="space-y-1 max-h-[50vh] overflow-y-auto pr-1">
        {items.map((it, i) => (
          <div
            key={i}
            className="grid grid-cols-[80px_1fr_140px_auto] gap-2 items-center bg-black border border-white/10 rounded-lg px-2 py-1.5"
            data-testid="chords-editor-row"
          >
            <input
              type="number"
              step="0.01"
              min="0"
              value={it.time}
              onChange={(e) => update(i, { time: parseFloat(e.target.value) || 0 })}
              className="bg-transparent border border-white/15 rounded px-2 py-1 text-xs tabular-nums text-white"
              data-testid="chords-editor-time"
            />
            <input
              type="text"
              value={it.chord}
              onChange={(e) => update(i, { chord: e.target.value })}
              className="bg-transparent border border-white/15 rounded px-2 py-1 text-sm font-bold text-white"
              data-testid="chords-editor-chord"
            />
            <select
              value={it.section || ""}
              onChange={(e) => update(i, { section: e.target.value || undefined })}
              className="bg-black border border-white/15 rounded px-1 py-1 text-xs text-white"
              data-testid="chords-editor-section"
            >
              <option value="">—</option>
              {SECTION_PRESETS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <div className="flex items-center gap-1">
              <IconBtn onClick={() => move(i, -1)} title="Monter" disabled={i === 0}>
                <ArrowUp size={14} />
              </IconBtn>
              <IconBtn onClick={() => move(i, +1)} title="Descendre" disabled={i === items.length - 1}>
                <ArrowDown size={14} />
              </IconBtn>
              <IconBtn onClick={() => insertAfter(i)} title="Insérer après">
                <Plus size={14} />
              </IconBtn>
              <IconBtn onClick={() => remove(i)} title="Supprimer">
                <Trash2 size={14} />
              </IconBtn>
            </div>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-neutral-500 text-center">
        Marque la <b>première ligne</b> d&apos;une partie avec la section appropriée — le PDF regroupe ensuite automatiquement.
      </p>
    </div>
  );
};

const IconBtn = ({ children, ...rest }) => (
  <button
    type="button"
    className="w-7 h-7 rounded-md border border-white/15 flex items-center justify-center hover:border-white/30 disabled:opacity-30"
    {...rest}
  >
    {children}
  </button>
);

const ActionBar = ({ isEditing, onToggleEdit, onAnalyzeAgain, onSaveAndPdf, onOpenPdf, onMail }) => (
  <div className="space-y-2">
    <div className="grid grid-cols-3 gap-2">
      <button
        onClick={onAnalyzeAgain}
        className="rounded-xl bg-black border border-white/15 text-white py-2.5 text-xs font-bold uppercase tracking-wider hover:border-white/30 flex items-center justify-center gap-1.5"
        data-testid="chords-action-analyze"
      >
        <Wand2 size={13} /> Analyser
      </button>
      <button
        onClick={onSaveAndPdf}
        className="rounded-xl bg-[var(--gl-gold)] text-black py-2.5 text-xs font-bold uppercase tracking-wider hover:brightness-110 flex items-center justify-center gap-1.5"
        data-testid="chords-action-save-pdf"
      >
        <Save size={13} /> Sauvegarder / PDF
      </button>
      <button
        onClick={onToggleEdit}
        className={`rounded-xl border py-2.5 text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 ${
          isEditing
            ? "bg-[var(--gl-neon)] text-black border-[var(--gl-neon)]"
            : "bg-black text-white border-white/15 hover:border-white/30"
        }`}
        data-testid="chords-action-edit"
      >
        {isEditing ? <X size={13} /> : <Pencil size={13} />} {isEditing ? "Terminer" : "Éditer"}
      </button>
    </div>
    <div className="grid grid-cols-3 gap-2">
      <button
        onClick={() => window.print()}
        className="rounded-lg bg-black border border-white/10 text-white/80 py-2 text-[11px] font-bold uppercase tracking-wider hover:border-white/25 flex items-center justify-center gap-1.5"
        data-testid="chords-action-print"
      >
        <Printer size={12} /> Imprimer
      </button>
      <button
        onClick={onOpenPdf}
        className="rounded-lg bg-black border border-[var(--gl-gold)]/40 text-[var(--gl-gold)] py-2 text-[11px] font-bold uppercase tracking-wider hover:bg-[var(--gl-gold)] hover:text-black flex items-center justify-center gap-1.5"
        data-testid="chords-action-export"
      >
        <Download size={12} /> Export PDF
      </button>
      <button
        onClick={onMail}
        className="rounded-lg bg-black border border-white/10 text-white/80 py-2 text-[11px] font-bold uppercase tracking-wider hover:border-white/25 flex items-center justify-center gap-1.5"
        data-testid="chords-action-mail"
      >
        <Mail size={12} /> Envoyer
      </button>
    </div>
  </div>
);

const groupBySection = (items) => {
  const groups = [];
  let current = { name: "", items: [] };
  for (const it of items) {
    if (it.section) {
      if (current.items.length || current.name) groups.push(current);
      current = { name: it.section, items: [] };
    }
    current.items.push(it);
  }
  groups.push(current);
  return groups.filter((g) => g.items.length);
};

export default ChordsDialog;
